// overlapCheck.js — detect when a campaign's audience overlaps with active
// automations, so marketing can be warned before scheduling a double-push.
//
// Heuristic — intentionally lenient so we surface likely overlaps without
// requiring a full segment-satisfiability solver:
//   - Inactivity automation vs. any campaign rule on `lastOrderDaysAgo`
//   - Schedule/registeredAt automation with requireZeroOrders vs. a campaign
//     targeting zero-order / new-lifecycle users
//   - Schedule/firstPlaceSavedAt automation with requireZeroOrders vs. same
//   - lifecycleChange automation vs. a campaign filtering on the same lifecycleStage
//   - Any automation carrying its own ruleTree vs. shared literal rules on the campaign
//
// Returns [{ automation, reasons: string[] }], one entry per overlapping automation.

// Walk a ruleTree and collect leaf `{ field, op, value }` rules across all groups.
const flattenRules = (tree) => {
  if (!tree || !Array.isArray(tree.rules)) return [];
  const out = [];
  const walk = (node) => {
    for (const r of node.rules || []) {
      if (r && Array.isArray(r.rules)) walk(r);           // nested group
      else if (r && r.field && r.op) out.push(r);
    }
  };
  walk(tree);
  return out;
};

const rulesInclude = (rules, field, opFilter, valueFilter) => rules.some(r =>
  r.field === field
  && (!opFilter || r.op === opFilter)
  && (valueFilter === undefined || String(r.value) === String(valueFilter))
);

export const detectOverlaps = (campaignRuleTree, activeAutomations) => {
  const rules = flattenRules(campaignRuleTree);
  if (rules.length === 0 || !Array.isArray(activeAutomations)) return [];

  const overlaps = [];

  for (const auto of activeAutomations) {
    const t = auto.trigger || {};
    const cfg = t.config || {};
    const reasons = [];

    // Inactivity automation → any campaign rule on lastOrderDaysAgo is a signal
    if (t.type === 'inactivity' && rulesInclude(rules, 'lastOrderDaysAgo')) {
      const threshold = cfg.lastOrderDaysAgo;
      reasons.push(
        typeof threshold === 'number'
          ? `both target inactive users (automation fires at ${threshold}d)`
          : 'both target inactive users'
      );
    }

    // Zero-order-gated schedule automations vs. new/zero-order campaign filters
    const isRegisterAnchor = t.type === 'schedule' && (cfg.anchor === 'registeredAt' || cfg.anchor === 'firstPlaceSavedAt');
    if (isRegisterAnchor && cfg.requireZeroOrders) {
      if (rulesInclude(rules, 'totalOrders', 'eq', 0)) reasons.push('both target zero-order users');
      if (rulesInclude(rules, 'lifecycleStage', 'eq', 'new')) reasons.push('both target the "new" lifecycle stage');
    }

    // lifecycleChange automation → campaign filtering on the same `to` stage
    if (t.type === 'lifecycleChange' && cfg.to && rulesInclude(rules, 'lifecycleStage', 'eq', cfg.to)) {
      reasons.push(`both target the "${cfg.to}" lifecycle stage`);
    }

    // Automation carrying its own ruleTree → shared literal rules
    if (auto.ruleTree) {
      const autoRules = flattenRules(auto.ruleTree);
      const shared = new Set();
      for (const cr of rules) {
        for (const ar of autoRules) {
          if (cr.field === ar.field && cr.op === ar.op && String(cr.value) === String(ar.value)) {
            shared.add(cr.field);
          }
        }
      }
      if (shared.size > 0) reasons.push(`shared segment rules: ${[...shared].join(', ')}`);
    }

    if (reasons.length > 0) overlaps.push({ automation: auto, reasons });
  }
  return overlaps;
};

// Test helpers exposed for unit-style validation from a Node script.
export const _internals = { flattenRules, rulesInclude };
